import {
  SmartEdgeOptions,
  getSmartEdge,
  pathfindingJumpPointNoDiagonal,
  svgDrawSmoothLinePath,
} from '@tisoap/react-flow-smart-edge';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import {EdgeProps, SmoothStepEdge, getSmoothStepPath, useNodes, Node} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {MethodNode, ProcedureNodeType} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import {createNameIdentifierForMaterialEdgeTestKey} from 'test/lib/cypress/shared/ProceduresTestPropUtils';
import {BLUE_LIGHTER, GRAY, GRAY_DARK, GRAY_DARKER, RED} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getMaterialAndMethodNameFromCid} from './flowchartManagementUtils';
import {getMaterialCidFromHandle} from './handleIdentifierUtils';
import ProcedureEdgeLabelRenderer from './ProcedureEdgeLabelRenderer';
import {ProcedureEditorContext} from './ProcedureEditorContext';
import {MarkerEndId} from './ProcedureMarkerDefinition';
import {makeOpacityStyle} from './procedureMethodNodeCommonConsts';
import {FilteredProcedureMethodSection} from './ProcedureMethodSectionFilterDropdown';
import {ProcedureValidationContext} from './validation/ProcedureValidationContext';
import {filterProcedureValidationErrors} from './validation/procedureValidationUtils';

interface ProcedureMaterialEdgeRendererProps extends EdgeProps {}

const SmoothStepConfiguration: SmartEdgeOptions = {
  drawEdge: svgDrawSmoothLinePath,
  generatePath: pathfindingJumpPointNoDiagonal,
  fallback: SmoothStepEdge,
  gridRatio: 4, // Lower values produce more granular path finding
  nodePadding: 45, // X and Y padding around nodes
};

function getMaterialEdgeTestKey(
  sourceHandle: string,
  targetHandle: string,
  allMethodNodesInFlowchart: Array<MethodNode>
): string {
  const sourceMaterialCid = getMaterialCidFromHandle(sourceHandle);
  const targetMaterialCid = getMaterialCidFromHandle(targetHandle);
  const {methodName: sourceMethodName, materialName: sourceMaterialName} = getMaterialAndMethodNameFromCid(
    allMethodNodesInFlowchart,
    sourceMaterialCid
  );
  const {methodName: targetMethodName, materialName: targetMaterialName} = getMaterialAndMethodNameFromCid(
    allMethodNodesInFlowchart,
    targetMaterialCid
  );

  return createNameIdentifierForMaterialEdgeTestKey(
    sourceMethodName,
    sourceMaterialName,
    targetMethodName,
    targetMaterialName
  );
}

export default function ProcedureMaterialEdgeRenderer(
  props: ProcedureMaterialEdgeRendererProps
): JSX.Element {
  const {
    id,
    data,
    selected,
    sourcePosition,
    targetPosition,
    sourceX,
    sourceY,
    targetX,
    targetY,
    markerStart,
  } = props;

  const {allMethodNodesInFlowchart, doesMaterialEdgeHaveMatchingMethodEdge, sourceHandle, targetHandle} =
    data;
  const {accessLevel} = ProcedurePageContext.useContext();
  const {methodCidBeingDragged, selectedMethodSectionFilter, isProcedureReadOnly} =
    ProcedureEditorContext.useContext();

  const [isHovered, hoverProps] = useHoverState();
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const materialEdgeTestKey = getMaterialEdgeTestKey(sourceHandle, targetHandle, allMethodNodesInFlowchart);

  const hasValidationErrors =
    filterProcedureValidationErrors(procedureValidationErrors, {
      primaryObjectId: props.id,
    }).length > 0;

  const reactFlowNodes = useNodes().filter(
    (node): node is Node<MethodNode> => node.type === ProcedureNodeType.METHOD
  );

  const {edgeCenterX, edgeCenterY, svgPathString} = useMemo(
    () => {
      if (doesMaterialEdgeHaveMatchingMethodEdge) {
        const [svgPathString, edgeCenterX, edgeCenterY] = getSmoothStepPath({
          sourceX,
          sourceY,
          sourcePosition,
          targetX,
          targetY,
          targetPosition,
          borderRadius: 2,
        });
        return {edgeCenterX, edgeCenterY, svgPathString};
      }
      const getSmartEdgeResponse = getSmartEdge({
        sourcePosition,
        targetPosition,
        sourceX,
        sourceY,
        targetX,
        targetY,
        nodes: reactFlowNodes,
        options: SmoothStepConfiguration,
      });
      assert(getSmartEdgeResponse != null, 'Unable to find smart material edge');
      return getSmartEdgeResponse;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allMethodNodesInFlowchart, doesMaterialEdgeHaveMatchingMethodEdge]
  );

  const ICON_LOCATION_CORRECTION = 10;
  const markerEndUrl = hasValidationErrors
    ? MarkerEndId.INVALID_MATERIAL_EDGE_MARKER
    : selected && !isProcedureReadOnly
      ? MarkerEndId.SELECTED_MATERIAL_EDGE_MARKER
      : MarkerEndId.VALID_EDGE_MARKER;

  return (
    <>
      {/* Hidden path selector makes it easier to click on dashed material edges */}

      {selectedMethodSectionFilter.includes(FilteredProcedureMethodSection.MATERIALS) && (
        <>
          {/* Hidden path selector makes it easier to click on dashed material edges */}
          <path
            {...hoverProps}
            className={classNames(
              'react-flow__edge-path-selector',
              css(styles.hiddenSelectorEdge, isProcedureReadOnly && styles.unSelectable)
            )}
            d={svgPathString}
          />
          <path
            {...hoverProps}
            className={classNames(
              'react-flow__edge-path',
              css(
                styles.visibleEdge,
                isProcedureReadOnly && styles.unSelectable,
                selected && !isProcedureReadOnly && styles.isSelected,
                hasValidationErrors && styles.hasValidationErrors,
                makeOpacityStyle(methodCidBeingDragged)
              )
            )}
            d={svgPathString}
            markerEnd={`url(#${markerEndUrl})`}
            markerStart={markerStart}
            {...getTestProps({
              component: 'ProcedureMaterialEdgeRenderer',
              element: 'root',
              key: materialEdgeTestKey,
            })}
          />
          <ProcedureEdgeLabelRenderer>
            {/* Classes 'nodrag nopan' ensure ReactFlow doesn't allow certain behaviors with this edge */}
            {(isHovered || selected) &&
              !isProcedureReadOnly &&
              accessLevel === ProcedureAccessLevel.FULL_ACCESS && (
                <div
                  className={classNames(
                    'nodrag nopan',
                    css(styles.edgeLabel, makeOpacityStyle(methodCidBeingDragged))
                  )}
                  style={{
                    top: `${edgeCenterY - ICON_LOCATION_CORRECTION}px`,
                    left: `${edgeCenterX - ICON_LOCATION_CORRECTION}px`,
                  }}
                >
                  <div
                    {...hoverProps}
                    className={css(styles.deleteButton)}
                    onClick={(e) => {
                      e.stopPropagation();
                      data.onDeleteMaterialEdge(id);
                    }}
                    {...getTestProps({
                      component: 'ProcedureMaterialEdgeRenderer',
                      element: 'deleteButton',
                      key: materialEdgeTestKey,
                    })}
                  >
                    <BenchlingIcon className={css(styles.trashCan)} icon="delete" />
                  </div>
                </div>
              )}
          </ProcedureEdgeLabelRenderer>
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  visibleEdge: {
    stroke: GRAY_DARK,
    strokeWidth: '1.5px',
    strokeDasharray: '4px',
  },
  hiddenSelectorEdge: {
    fill: 'none',
    stroke: 'transparent',
    strokeWidth: 28,
  },
  edgeLabel: {
    position: 'absolute',
    pointerEvents: 'all',
  },
  deleteButton: {
    background: GRAY,
    cursor: 'pointer',
    borderRadius: '2px',
    display: 'inline-flex',
    alignItems: 'flex-start',
    padding: '2px',
    position: 'absolute',
  },
  trashCan: {
    color: GRAY_DARKER,
    width: '12px',
    height: '12px',
    padding: '2px',
  },
  isSelected: {
    stroke: BLUE_LIGHTER,
    strokeWidth: '2px',
  },
  unSelectable: {
    select: 'none',
  },
  hasValidationErrors: {
    stroke: RED,
    strokeWidth: '2px',
  },
});
